import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Switch, Route } from 'react-router-dom';
import { decodePassengers, isOldSearchPassengersFormat, isDesktopSize } from 'utils/Reserbus';
import {
  searchTracker,
  trackTripPriceAlerts,
  viewResultsTracker,
} from 'metrics/user-analytics/search';
import { parseLodgingCheckboxParam } from 'utils/lodgingCheckboxAnalytics';
import Header from 'components/Header';
import SearchBar from 'components/search/SearchBar';
import Providers from 'components/search/Providers';
import ProviderDepartures from 'components/search/ProviderDepartures';
import ProviderReturns from 'components/search/ProviderReturns';
import LoadingScreen from 'components/LoadingScreen';
import { searchedPassengersCanBeFiltered } from 'utils/passengers';
import { PRICE_ALERT_REDIRECTED } from 'constants/TrackEvents';
import Footer from '../../../ui/atoms/Footer';
import LoadingRedirect from '../LoadingRedirect';
import { getMomentFromSearchDateParam } from '../../../utils/search/searchDateParam';
import withWhitelabelTheme from '../../../hocs/whitelabel/withWhitelabelTheme';
import SearchModal from '../../../ui/atoms/SearchModal';
import AppDownloadModal from '../../../ui/atoms/AppDownloadModal';

const propTypes = {
  match: PropTypes.shape({
    path: PropTypes.string.isRequired,
  }).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
  }).isRequired,
  history: PropTypes.object.isRequired,
  originSlug: PropTypes.string.isRequired,
  destinationSlug: PropTypes.string.isRequired,
  headerData: PropTypes.shape({
    homeButton: PropTypes.bool.isRequired,
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.string.isRequired,
    origin: PropTypes.string.isRequired,
    destination: PropTypes.string.isRequired,
    dates: PropTypes.string.isRequired,
  }).isRequired,
  isReturns: PropTypes.string.isRequired,
  departureDate: PropTypes.string.isRequired,
  returnDate: PropTypes.string,
  passengers: PropTypes.string.isRequired,
  roundTrip: PropTypes.bool.isRequired,
  searchIsFetching: PropTypes.bool.isRequired,
  searchIsPolling: PropTypes.bool.isRequired,
  analyticsData: PropTypes.object.isRequired,
  setSearchParams: PropTypes.func.isRequired,
  createSearch: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  fetchCoupon: PropTypes.func.isRequired,
  couponLoading: PropTypes.bool,
  couponCode: PropTypes.string,
  couponCodeParam: PropTypes.string,
  searchHasRedirectTrips: PropTypes.bool,
  features: PropTypes.object.isRequired,
  returnIsOpenTicket: PropTypes.bool,
  returnId: PropTypes.string,
  departureId: PropTypes.string,
  returnDidInvalidate: PropTypes.bool,
  departureDidInvalidate: PropTypes.bool,
  setSortOrder: PropTypes.func.isRequired,
  resetPurchase: PropTypes.func.isRequired,
  includeRecommendedTrips: PropTypes.bool,
  resetPayment: PropTypes.func.isRequired,
  setCategoriesFilter: PropTypes.func,
  currentTripList: PropTypes.array,
  departureIsOpenTicket: PropTypes.bool,
  fetchFrequentPassengers: PropTypes.func.isRequired,
  updateDepartureIsOpen: PropTypes.func,
  setLodgingCheckboxSession: PropTypes.func.isRequired,
};

class SearchProcessor extends Component {
  constructor(props) {
    super(props);
    this.state = {
      viewResultsEventCalled: false,
      isMobileSize: !isDesktopSize(),
    };
    // Cached before cleanAnalyticsParams() strips analytics_* on results mount.
    this.entrySearchQuery = '';

    // Create a MediaQueryList to monitor screen size changes
    this.mediaQuery = window.matchMedia('only screen and (min-width: 1081px)');
    this.handleMediaQueryChange = this.handleMediaQueryChange.bind(this);
  }

  componentDidMount() {
    const {
      setSortOrder,
      features,
      resetPurchase,
      resetPayment,
      passengers,
      fetchFrequentPassengers,
    } = this.props;
    this.syncEntrySearchQuery();
    this.captureLodgingCheckboxSession();
    resetPurchase();
    resetPayment();
    fetchFrequentPassengers();
    this.usingNerbyTerminals = new URLSearchParams(window.location.href).get('usingNerbyTerminals');
    this.usingAutoSelectOrigin = new URLSearchParams(window.location.href).get(
      'usingAutoSelectOrigin',
    );

    if (this.mediaQuery.addEventListener) {
      this.mediaQuery.addEventListener('change', this.handleMediaQueryChange);
    } else {
      this.mediaQuery.addListener(this.handleMediaQueryChange);
    }

    // Ordering results automatically by price if the feature is enabled
    if (features.SORT_TRIPS_BY_PRICE) {
      setSortOrder('price');
    }

    if (passengers) this.setPassengersFilters({ passengers });
    this.setSearchParams();
    this.createSearch();
  }

  componentWillReceiveProps(nextProps) {
    const { searchIsFetching, searchIsPolling, fetchFrequentPassengers } = this.props;
    const { viewResultsEventCalled } = this.state;
    const queryParams = this.getEntryQueryParams();

    if (searchIsFetching && !nextProps.searchIsFetching) {
      searchTracker(nextProps.analyticsData.search, queryParams);
      fetchFrequentPassengers();
    }
    if (searchIsPolling && !nextProps.searchIsPolling && !viewResultsEventCalled) {
      const hasRedirectTrips = nextProps.searchHasRedirectTrips;

      const lowestPrice =
        nextProps.currentTripList.length > 0
          ? nextProps.currentTripList.reduce((min, obj) => {
              return obj.pricing.total < min ? obj.pricing.total : min;
            }, nextProps.currentTripList[0].pricing.total)
          : 0;

      this.sendToViewResultsTracker(
        nextProps.analyticsData,
        queryParams,
        hasRedirectTrips,
        lowestPrice,
      );
    }
  }

  componentDidUpdate(prevProps) {
    const {
      originSlug,
      destinationSlug,
      departureDate,
      returnDate,
      passengers,
      location,
      history: { action },
    } = this.props;
    const originChanged = originSlug !== prevProps.originSlug;
    const destinationChanged = destinationSlug !== prevProps.destinationSlug;
    const departureChanged = departureDate !== prevProps.departureDate;
    const returnChanged = returnDate !== prevProps.returnDate;
    const passengersChanged = passengers !== prevProps.passengers;
    const searchChanged =
      originChanged || destinationChanged || departureChanged || returnChanged || passengersChanged;
    /**
     * If the user changes the search params, we need to create a new search
     * this can happen when the user uses the SearchWidget or the DaysControls.
     * This condition also is true when the user uses the browser's navigation buttons
     *
     */
    if (
      (searchChanged && action === 'POP') ||
      (searchChanged && action === 'PUSH') ||
      (searchChanged && action === 'REPLACE')
    ) {
      // Returning this state to false allows us to track the ViewResults event again
      // for the next search.
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ viewResultsEventCalled: false });
      this.syncEntrySearchQuery(location.search);
      this.captureLodgingCheckboxSession();
      this.setPassengersFilters({ passengers });
      this.setSearchParams();
      this.createSearch();
    }
  }

  componentWillUnmount() {
    const { analyticsData, searchIsFetching, searchIsPolling, searchHasRedirectTrips } = this.props;
    const { viewResultsEventCalled } = this.state;

    if (this.mediaQuery.removeEventListener) {
      this.mediaQuery.removeEventListener('change', this.handleMediaQueryChange);
    } else {
      // Support for older browsers
      this.mediaQuery.removeListener(this.handleMediaQueryChange);
    }

    if (searchIsPolling && !searchIsFetching && !viewResultsEventCalled) {
      this.sendToViewResultsTracker(
        analyticsData,
        this.getEntryQueryParams(),
        searchHasRedirectTrips,
      );
    }
  }

  /**
   * Handles screen size changes
   */
  handleMediaQueryChange() {
    this.setState({
      isMobileSize: !isDesktopSize(),
    });
  }

  /**
   * Set the categories filters based on the passengers for the search
   * @param {Object} params - function params.
   * @param {String} params.passengers - passengers used for the search
   */
  setPassengersFilters({ passengers }) {
    const { setCategoriesFilter, features } = this.props;
    const decodedValidPassengersCategories =
      passengers &&
      searchedPassengersCanBeFiltered({ searchPassengers: decodePassengers(passengers) });
    const isOldPassengers = isOldSearchPassengersFormat(passengers);

    if (isOldPassengers) return;

    if (!decodedValidPassengersCategories) {
      if (features.FREE_FARE_BENEFIT_DENIAL) setCategoriesFilter({ categories: [] });
      return;
    }

    const passengersFilters = passengers.split(',').reduce((acc, passenger) => {
      acc.push(passenger.split('-')[0]);
      return acc;
    }, []);
    setCategoriesFilter({ categories: passengersFilters });
  }

  setSearchParams() {
    const {
      originSlug,
      destinationSlug,
      passengers,
      roundTrip,
      setSearchParams,
      departureDate,
      returnDate,
      departureIsOpenTicket,
      returnIsOpenTicket,
    } = this.props;
    const departs = getMomentFromSearchDateParam(departureDate).format('DD-MM-YYYY');
    const returns = roundTrip
      ? getMomentFromSearchDateParam(returnDate).format('DD-MM-YYYY')
      : null;

    let hybridTrip = 'none';
    if (departureIsOpenTicket && returnIsOpenTicket) {
      hybridTrip = 'both';
    } else if (departureIsOpenTicket) {
      hybridTrip = 'departure';
    } else if (returnIsOpenTicket) {
      hybridTrip = 'return';
    }

    setSearchParams(
      originSlug,
      destinationSlug,
      decodePassengers(passengers),
      departs,
      returns,
      hybridTrip,
    );
  }

  getEntryQueryParams() {
    const { location } = this.props;
    return new URLSearchParams(this.entrySearchQuery || location.search);
  }

  captureLodgingCheckboxSession() {
    const { setLodgingCheckboxSession } = this.props;
    setLodgingCheckboxSession(parseLodgingCheckboxParam(this.getEntryQueryParams()));
  }

  syncEntrySearchQuery(search) {
    const { location } = this.props;
    this.entrySearchQuery = search ?? location.search;
  }

  sendToViewResultsTracker(analyticsData, queryParams, searchHasRedirectTrips, lowestPrice = '') {
    queryParams.append('usingNerbyTerminals', Boolean(this.usingNerbyTerminals));
    queryParams.append('usingAutoSelectOrigin', Boolean(this.usingAutoSelectOrigin));
    const isTripAlertRedirect = queryParams.get('trip_alert');
    this.setState({ viewResultsEventCalled: true });
    viewResultsTracker(analyticsData, queryParams, searchHasRedirectTrips);
    if (isTripAlertRedirect) {
      trackTripPriceAlerts(PRICE_ALERT_REDIRECTED, analyticsData.search, isTripAlertRedirect, {
        lowestPrice,
      });
    }
  }

  createSearch() {
    const {
      originSlug,
      destinationSlug,
      passengers,
      roundTrip,
      createSearch,
      departureDate,
      returnId,
      departureId,
      returnDate,
      returnDidInvalidate,
      departureDidInvalidate,
      returnIsOpenTicket,
      includeRecommendedTrips,
      departureIsOpenTicket,
      updateDepartureIsOpen,
    } = this.props;
    const departs = getMomentFromSearchDateParam(departureDate).format('DD-MM-YYYY');
    const returns = roundTrip
      ? getMomentFromSearchDateParam(returnDate).format('DD-MM-YYYY')
      : null;

    if (!departureId || departureDidInvalidate) {
      createSearch(
        'departure',
        originSlug,
        destinationSlug,
        departs,
        passengers,
        roundTrip,
        departureIsOpenTicket,
        includeRecommendedTrips,
      );
    }

    if (updateDepartureIsOpen) {
      updateDepartureIsOpen(departureIsOpenTicket);
    }

    if (roundTrip && (!returnId || returnDidInvalidate)) {
      createSearch(
        'return',
        destinationSlug,
        originSlug,
        returns,
        passengers,
        roundTrip,
        returnIsOpenTicket,
        includeRecommendedTrips,
      );
    }
  }

  render() {
    const {
      searchIsFetching,
      headerData,
      isReturns,
      match,
      history,
      fetchCoupon,
      couponLoading,
      couponCode,
      couponCodeParam,
      t,
      features,
    } = this.props;
    const { isMobileSize } = this.state;

    if (searchIsFetching) return <LoadingScreen resultMessages />;

    if (!couponCode && !couponLoading && couponCodeParam) {
      fetchCoupon(couponCodeParam, t);
    }

    const queryParams = new URLSearchParams(window.location.search);
    const isExchange = queryParams.get('isExchange');

    const showDesktopsSearchBar = !isExchange && !isMobileSize;
    const showMobileSearchBar = !isExchange && isMobileSize;

    return (
      <>
        <div className="l-app">
          <Header
            searchFixed
            homeButton={headerData.homeButton}
            backButton={!headerData.homeButton}
            aditionalFixedContent={showDesktopsSearchBar && <SearchBar history={history} />}
            showLogo={!isReturns}
          >
            {showMobileSearchBar ? (
              <SearchModal title={t('trips:title_search_modal')} history={history} />
            ) : null}
          </Header>

          {features.TRIP_REDIRECTION_LEAD && <LoadingRedirect />}

          <Switch>
            <Route
              path={`${match.path}/:departureId?/:way(departure|return)?/providers`}
              component={Providers}
            />

            <Route
              path={`${match.path}/:way(departures)/:isOpen(open)`}
              component={ProviderDepartures}
            />

            <Route
              path={`${match.path}/:way(departures)/:providerId?`}
              component={ProviderDepartures}
            />

            <Route
              path={`${match.path}/:departureId/:way(returns)/:isOpen(open)`}
              component={ProviderReturns}
            />

            <Route
              path={`${match.path}/:departureId/:way(returns)/:providerId?`}
              component={ProviderReturns}
            />
          </Switch>
        </div>

        <AppDownloadModal />

        {features.SHOW_SEARCH_FOOTER && <Footer />}
      </>
    );
  }
}

SearchProcessor.propTypes = propTypes;

export default withTranslation('trips')(withWhitelabelTheme(SearchProcessor));
